import React, { useState, useEffect, useRef } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../UI/dialog';
import { Button } from '../UI/button';
import { X, RotateCw, Check, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function ImageCropperDialog({
    isOpen,
    onClose,
    file,
    onCropComplete,
    title = "Crop Business Card",
    aspect = null
}) {
    const [imageSrc, setImageSrc] = useState('');
    const [imgLoading, setImgLoading] = useState(true);
    const [crop, setCrop] = useState();

    const imageRef = useRef(null);

    // Load file as object URL
    useEffect(() => {
        if (!file) return;
        setImgLoading(true);
        const objectUrl = URL.createObjectURL(file);
        setImageSrc(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [file]);

    // Handle image load to set initial crop box size and position
    const handleImageLoaded = (e) => {
        setImgLoading(false);
        const { width, height } = e.currentTarget;

        let cropW, cropH;
        if (aspect) {
            const minDim = Math.min(width, height);
            cropW = Math.round(minDim * 0.8);
            cropH = Math.round(cropW / aspect);
            if (cropH > height) {
                cropH = Math.round(height * 0.8);
                cropW = Math.round(cropH * aspect);
            }
        } else {
            cropW = Math.round(width * 0.8);
            cropH = Math.round(height * 0.8);
        }

        const cropX = Math.round((width - cropW) / 2);
        const cropY = Math.round((height - cropH) / 2);

        setCrop({
            unit: 'px',
            x: cropX,
            y: cropY,
            width: cropW,
            height: cropH,
            ...(aspect ? { aspect } : {})
        });
    };

    // Rotate the image 90 degrees clockwise on a canvas
    const handleRotate = async () => {
        if (!imageRef.current) return;
        setImgLoading(true);

        const img = new Image();
        img.src = imageSrc;

        await new Promise((resolve) => {
            img.onload = resolve;
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.height;
        canvas.height = img.width;

        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((90 * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        canvas.toBlob((blob) => {
            if (blob) {
                const objectUrl = URL.createObjectURL(blob);
                setImageSrc(objectUrl);
            }
        }, file.type || 'image/png');
    };

    // Perform actual crop operation and return file
    const handleApply = async () => {
        if (!imageRef.current || !crop) return;

        const image = imageRef.current;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        let canvasW = Math.round(crop.width * scaleX);
        let canvasH = Math.round(crop.height * scaleY);

        // When aspect ratio is specified, strictly enforce the aspect ratio on output canvas
        if (aspect) {
            const side = Math.max(canvasW, canvasH);
            canvasW = side;
            canvasH = Math.round(side / aspect);
        }

        const canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;

        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height
        );

        canvas.toBlob((blob) => {
            if (blob) {
                const croppedFile = new File([blob], file.name || 'cropped_card.png', {
                    type: file.type || 'image/png',
                    lastModified: Date.now()
                });
                onCropComplete(croppedFile);
            } else {
                toast.error("Failed to crop the image.");
            }
        }, file.type || 'image/png');
    };

    return (
        <Dialog mobilePopup={false} open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                overlayClassName="image-cropper-dialog-overlay !z-[10020]"
                className="image-cropper-dialog w-screen max-w-[100vw] h-full sm:max-w-[650px] p-0 flex flex-col rounded-none sm:rounded-lg overflow-x-hidden bg-[#F1F1F0] gap-0 !z-[10021]"
                data-image-cropper=""
            >
                <style>{`
                    /* Disable marching ants animation and use solid premium border */
                    .ReactCrop__crop-selection {
                        border: 1.5px solid #ffffff !important;
                        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.4) !important;
                        background: none !important;
                        animation: none !important;
                    }
                    /* Styling handles to look standard and clean */
                    .ReactCrop__drag-handle {
                        background-color: #ffffff !important;
                        border: 1px solid #000000 !important;
                        border-radius: 4px !important;
                        width: 12px !important;
                        height: 12px !important;
                    }
                `}</style>

                <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4 !sm:pb-4 shrink-0 bg-white border-none">
                    {/* Mobile Header */}
                    <div className="sm:hidden flex items-center justify-between w-full h-8">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center" onClick={onClose}>
                                <ArrowLeft className="h-6 w-6" />
                                <span className="sr-only">Back</span>
                            </DialogClose>
                            <DialogTitle className="text-lg font-medium">
                                <div className="truncate">
                                    {title}
                                </div>
                            </DialogTitle>
                        </div>
                    </div>
                    {/* Desktop Header */}
                    <div className="hidden sm:flex items-center justify-between w-full pb-4">
                        <DialogTitle className="text-lg md:text-xl flex items-center gap-3">
                            <span>{title}</span>
                        </DialogTitle>
                        <DialogClose className="flex-shrink-0" tabIndex={-1} onClick={onClose}>
                            <X className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </DialogClose>
                    </div>
                </DialogHeader>

                {/* Unified Content Scroll Area with zero padding for full-width view */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden max-h-[calc(100vh-140px)] sm:max-h-[75vh] flex flex-col justify-between w-full bg-[#F1F1F0]">
                    <div className="w-full flex-1 flex flex-col items-center justify-center bg-[#F1F1F0] select-none overflow-x-hidden">
                        {imgLoading && (
                            <div className="absolute z-15 flex flex-col items-center gap-2 bg-[#F1F1F0] p-4 rounded-lg shadow-sm border border-gray-150">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent border-[var(--sahi-primary-color)]"></div>
                                <span className="text-sm text-gray-500 font-medium">Loading editor...</span>
                            </div>
                        )}

                        {imageSrc && (
                            <div className="w-full max-w-full flex items-center justify-center p-0 overflow-x-hidden">
                                <ReactCrop
                                    crop={crop}
                                    onChange={(c) => setCrop(c)}
                                    aspect={aspect}
                                    className="max-w-full max-h-[52vh] sm:max-h-[48vh] overflow-hidden"
                                >
                                    <img
                                        ref={imageRef}
                                        src={imageSrc}
                                        alt="Source"
                                        onLoad={handleImageLoaded}
                                        style={{ maxHeight: '50vh', maxWidth: '100%', width: 'auto', height: 'auto', display: 'block' }}
                                        className="select-none pointer-events-none"
                                    />
                                </ReactCrop>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Controls at the absolute bottom */}
                <div className="px-4 py-4 bg-[#F1F1F0] shrink-0 border-t border-gray-100 w-full mt-auto overflow-x-hidden">
                    {/* Mobile View Buttons: Rotate & Apply side-by-side in one line at the absolute bottom */}
                    <div className="flex flex-row gap-3 w-full sm:hidden">
                        <Button
                            type="button"
                            disabled={imgLoading}
                            onClick={handleApply}
                            className="flex-1 h-10 text-sm flex items-center justify-center gap-1.5 font-semibold"
                        >
                            <Check className="h-4 w-4" strokeWidth={3} />
                            Apply
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={imgLoading}
                            onClick={handleRotate}
                            className="flex-1 h-10 text-sm flex items-center justify-center gap-1.5 font-semibold"
                        >
                            <RotateCw className="h-3.5 w-3.5" />
                            Rotate
                        </Button>
                    </div>

                    {/* Desktop View Buttons */}
                    <div className="hidden sm:flex items-center gap-3 w-full">
                        <Button
                            type="button"
                            disabled={imgLoading}
                            onClick={handleApply}
                            className="h-10 px-5 text-sm font-semibold flex items-center justify-center gap-1.5"
                        >
                            <Check className="h-4 w-4" strokeWidth={3} />
                            Apply
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={imgLoading}
                            onClick={handleRotate}
                            className="h-10 px-4 text-sm flex items-center justify-center gap-1.5 font-medium"
                        >
                            <RotateCw className="h-3.5 w-3.5" />
                            Rotate
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="sahi-secondary-button h-10 px-4 text-sm flex items-center justify-center gap-1.5 font-medium"
                        >
                            <X className="h-4 w-4" />
                            Cancel
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
